import { Response } from 'types/request'
import { createErrorTransformer } from 'store/queries/utils.ts'
import { Flow } from 'types/logging.ts'
import { boltBackend } from '../index.ts'
import { BOLT_BACKEND_TAGS } from 'store/queries/bolt-backend/constants.ts'
import { roundToTwoDecimals } from 'utils/number.ts'
import type { GetBillShockResponse, GetBillShockPayload } from './types.ts'
import { calculateInstalmentRatios } from 'utils/instalment.ts'
import {
  BillShockFutureInstalment,
  CONTRACT_STATUSES,
  GetBillShockDataResponseBody,
  ServiceContractElectricity,
  ServiceContractGas
} from '@boltenergy-be/shared'
import { newRequest } from 'utils/request.ts'
import { generateBillShockAxiosRequestConfig } from './utils.ts'

export const billingContractsBoltBackend = boltBackend.injectEndpoints({
  endpoints: (builder) => ({
    // GET BILL SHOCK
    getBillShock: builder.query<GetBillShockResponse | undefined, GetBillShockPayload>({
      query: ({ billingContractId, serviceContracts, email, proposedInstalmentFromUrl }) => {
        let elInstalment = roundToTwoDecimals(Number(serviceContracts.electricity.detail.instalment))
        let gasInstalment = serviceContracts.gas ? roundToTwoDecimals(Number(serviceContracts.gas.detail.instalment)) : 0
        const { electricity: elRatio, gas: gasRatio } = calculateInstalmentRatios(elInstalment, gasInstalment)

        if (Number(proposedInstalmentFromUrl)) {
          if (elRatio === 1) {
            elInstalment = Number(proposedInstalmentFromUrl)
          } else {
            elInstalment = roundToTwoDecimals(elRatio * Number(proposedInstalmentFromUrl))
            gasInstalment = roundToTwoDecimals(gasRatio * Number(proposedInstalmentFromUrl))
          }
        }

        return {
          method: 'POST',
          url: `/resources/billing-contracts/${billingContractId}/bill-shock`,
          body: {
            email,
            instalments: [
              {
                contractId: serviceContracts.electricity.contractNumber,
                instalment: elInstalment
              },
              ...(serviceContracts.gas
                ? [
                    {
                      contractId: serviceContracts.gas.contractNumber,
                      instalment: gasInstalment
                    }
                  ]
                : [])
            ]
          }
        }
      },
      transformResponse: async (
        { success, data }: Response<GetBillShockDataResponseBody>,
        _meta: unknown,
        args: GetBillShockPayload
      ): Promise<GetBillShockResponse | undefined> => {
        let returnData: GetBillShockResponse | undefined = undefined

        if (!success || !data?.billShock) {
          throw Error('get bill shock failed')
        }

        if (data?.billShock) {
          returnData = {
            billShock: data.billShock
          }

          // add adjusted bill shock if proposed instalment is less than 20
          if (data.billShock.instalment.proposed < 20) {
            const hasEffectiveGasContract =
              !!args.serviceContracts.gas && args.serviceContracts.gas.detail.status === CONTRACT_STATUSES.effective
            const adjustedElectricityContract: ServiceContractElectricity = {
              ...args.serviceContracts.electricity,
              detail: {
                ...args.serviceContracts.electricity.detail,
                instalment: hasEffectiveGasContract ? 10 : 20
              }
            }
            const adjustedGasContract: ServiceContractGas | undefined = hasEffectiveGasContract
              ? {
                  ...args.serviceContracts.gas!,
                  detail: {
                    ...args.serviceContracts.gas!.detail,
                    instalment: 10
                  }
                }
              : undefined

            // Re-fetch with a combined proposed instalment of minimum 20
            const { success: adjustedSuccess, data: adjustedData }: Response<GetBillShockDataResponseBody> = await newRequest(
              generateBillShockAxiosRequestConfig(args.billingContractId, args.email, adjustedElectricityContract, adjustedGasContract)
            )

            if (adjustedSuccess && adjustedData?.billShock) {
              const futureWithOriginalInstalment = adjustedData?.billShock.future.map((month: BillShockFutureInstalment) => ({
                ...month,
                plannedInstalment: data.billShock.instalment.current,
                proposedInstalment: Math.max(adjustedData.billShock?.instalment.current || 20, 20)
              }))

              returnData.adjustedBillShock = {
                ...adjustedData.billShock,
                future: futureWithOriginalInstalment
              }

              returnData.adjustedServiceContracts = {
                electricity: adjustedElectricityContract,
                gas: adjustedGasContract
              }
            }
          }
        }

        return returnData
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTRACTS}:getBillShock]`),
      providesTags: [BOLT_BACKEND_TAGS.billShock]
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetBillShockQuery } = billingContractsBoltBackend
